import React, { useState, useEffect } from "react";
import { isEmpty } from "lodash";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import Pagination from "../../components/Pagination/Pagination";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import CouponsAPI from "../../API/coupons.api";
import { getSessionFilters, getMappedFilters } from "../../utils/FilterHelpers";
import { setObject, getObject } from "../../utils/StorageHelpers";
import MissingData from "../../components/Shared/MissingData";
import CreateCouponOverlayModal from "../../components/OverlayModal/CreateCouponOverlayModal";
import CardTable from "../../components/Tables/CardTable";
import { COUPON_TYPES } from "../../utils/constants";
import { toCamelCase } from "../../utils/Helpers";
import BaseActionButton from "../../components/Buttons/BaseActionButton";
import AddPromoCodeModal from "../../components/Modals/AddPromoCodeModal";
import PromotionCodesAPI from "../../API/promotionCodes.api";
import { SuccessAlert } from "../../utils/Alerts";

const Coupons = () => {
    //General states
    const [isRendered, setIsRendered] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [isCouponsLoading, setIsCouponsLoading] = useState(true);
    //Coupons states
    const [coupons, setCoupons] = useState([]);
    const [couponsTotalCount, setCouponsTotalCount] = useState(0);
    const [couponsCurrentPage, setCouponsCurrentPage] = useState(1);
    const [couponsFiltersUsed, setCouponsFiltersUsed] = useState(false);
    const [couponsFilters, setCouponsFilters] = useState({
        name: "",
        productName: "",
        numberOfRedemptions: 0,
        type: null,
    });
    const [selectedCoupon, setSelectedCoupon] = useState("");

    const [couponsAppliedFilters, setCouponsAppliedFilters] = useState(() =>
        getSessionFilters("coupons-table-filters")
    );
    const [couponsSortBy, setCouponsSortBy] = useState(undefined);
    const [couponsSortDirection, setCouponsSortDirection] = useState(undefined);

    const [createCouponModal, setCreateCouponModal] = useState(false);
    const [addPromoModal, setAddPromoModal] = useState(false);

    const { t } = useTranslation();

    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Coupons-itemsPerPage", true) || 10
    );

    const history = useHistory();

    const headers = [
        { label: t("name"), name: "name" },
        { label: t("productName"), name: "productName", skipSort: true },
        {
            label: t("percentageDiscount"),
            name: "percentageDiscount",
            skipSort: true,
        },
        { label: t("createdAt"), name: "createdAt" },
        {
            label: t("numberOfPromotionCodes"),
            name: "numberOfPromotionCodes",
            skipSort: true,
        },
        {
            label: t("numberOfRedemptions"),
            name: "numberOfRedemptions",
        },
        {
            label: t("type"),
            name: "type",
        },
        { label: t("actions"), name: "actions", skipSort: true },
    ];

    const keys = [
        { name: "name" },
        { name: "productName" },
        { name: "percentageDiscount" },
        { name: "createdAt" },
        {
            name: "numberOfPromotionCodes",
        },
        {
            name: "numberOfRedemptions",
        },
        {
            name: "type",
        },
    ];

    const couponsSearchFilters = [
        {
            label: t("name"),
            name: "name",
            type: "Input",
            placeholder: t("couponNamePlaceholder"),
        },
        {
            label: t("productName"),
            name: "productName",
            type: "Input",
            placeholder: t("modelNamePlaceholder"),
        },
        {
            label: t("numberOfRedemptions"),
            name: "numberOfRedemptions",
            type: "Input",
            inputType: "number",
            placeholder: t("numberOfRedemptionsPlaceholder"),
        },
        {
            label: t("type"),
            name: "type",
            type: "Dropdown",
            options: [
                // { label: t(toCamelCase(COUPON_TYPES[0])), value: 0 },
                { label: t(toCamelCase(COUPON_TYPES[1])), value: 1 },
                { label: t(toCamelCase(COUPON_TYPES[2])), value: 2 },
            ],
        },
    ];

    useEffect(() => {
        const couponsLocalStorageFilters = getSessionFilters(
            "coupons-table-filters"
        );
        if (!isEmpty(couponsLocalStorageFilters)) {
            setCouponsFilters(couponsLocalStorageFilters);
            setCouponsAppliedFilters(couponsLocalStorageFilters);
        }

        setIsRendered(true);
        getData();
    }, []);

    const getData = () => {
        fetchData(couponsAppliedFilters, couponsSortBy, couponsSortDirection);
    };

    useEffect(() => {
        if (isRendered) {
            if (couponsSortBy && couponsSortDirection) {
                setCouponsCurrentPage(1);
                fetchData(
                    couponsAppliedFilters,
                    couponsSortBy,
                    couponsSortDirection
                );
            } else {
                setCouponsCurrentPage(1);
                fetchData(couponsAppliedFilters);
            }
        }
    }, [couponsSortBy, couponsSortDirection, isRendered]);

    const fetchData = async (
        _filters = null,
        _sortBy = null,
        _sortDirection = null,
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsCouponsLoading(true);
        const newFilter = getMappedFilters(_filters);
        const _offset = offset < 0 ? 0 : offset;

        let result = await CouponsAPI.getCoupons(
            itemsPerPage,
            newFilter,
            _offset,
            _sortBy,
            _sortDirection
        );
        setCoupons(mapCoupons(result?.data) || []);
        setCouponsTotalCount(result?.totalCount || 0);
        setIsCouponsLoading(false);
        if (initialLoading) {
            setInitialLoading(false);
        }
    };

    const increasePromos = async (payload) => {
        const result = await PromotionCodesAPI.createPromotionCodes(payload);
        if (result?.inserted) {
            SuccessAlert(
                t("addSuccessMessage", { entity: t("promotionCodes") })
            );
            setSelectedCoupon({});
            setAddPromoModal(false);
            setCouponsCurrentPage(1);
            fetchData(
                couponsAppliedFilters,
                couponsSortBy,
                couponsSortDirection
            );
        }
    };

    const mapCoupons = (data) =>
        data?.map((elem) => ({
            ...elem,
            createdAt: new Date(elem.createdAt),
            type: COUPON_TYPES[elem.type],
        }));

    const applyCouponsFilters = (applyFilters) => {
        setCouponsCurrentPage(1);
        fetchData(applyFilters, couponsSortBy, couponsSortDirection);
        setCouponsFiltersUsed(true);
    };

    const handleCouponsPageClick = (index) => {
        if (index !== couponsCurrentPage) {
            setCouponsCurrentPage(index);
            // call API to get coupons with the next pagination
            fetchData(
                couponsFilters,
                couponsSortBy,
                couponsSortDirection,
                index - 1
            );
        }
    };

    const isLoading = () => {
        if (isCouponsLoading) return true;
        return false;
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCouponsCurrentPage(1);
        setObject("Coupons-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        fetchData(
            couponsFilters,
            couponsSortBy,
            couponsSortDirection,
            0,
            itemsPerPage
        );
    };

    const renderActions = (row) => {
        return (
            <>
                <BaseActionButton
                    className={"table-btn table-btn-blue"}
                    onClick={() => {
                        setSelectedCoupon(row?.id);
                        setAddPromoModal(true);
                    }}
                    icon={"fas fa-plus"}
                    tippySettings={{
                        content: t("addPromoCodes"),
                    }}
                />
                <BaseActionButton
                    className={"table-btn table-btn-blue ml-2"}
                    onClick={() => {
                        history.push({
                            pathname: "/promotion-codes",
                            state: {
                                couponId: row?.id || "",
                                couponName: row?.name,
                            },
                        });
                    }}
                    icon={"fas fa-arrow-right"}
                    tippySettings={{
                        content: t("goPromoCodes"),
                    }}
                />
            </>
        );
    };

    return (
        <>
            <div id="coupons-content">
                <header className="main-content-header flex items-center justify-between">
                    <h1 className="h1">
                        <Trans i18nKey="coupons">Coupons</Trans>
                    </h1>
                    <div className="action-header-wrapper flex items-center">
                        <FilterDropdown
                            filterOptions={couponsSearchFilters}
                            filters={couponsFilters}
                            setFilters={setCouponsFilters}
                            applyFilters={applyCouponsFilters}
                            component={"coupons-table"}
                            appliedFilters={couponsAppliedFilters}
                            setAppliedFilters={setCouponsAppliedFilters}
                            disabled={isLoading()}
                        />

                        <button
                            onClick={() => setCreateCouponModal(true)}
                            className="default-orange-outline-btn ml-2"
                            id="upload-coupons-btn"
                        >
                            <i className="fas fa-plus mr-1.5" />
                            {t("newCoupon")}
                        </button>
                    </div>
                </header>
            </div>
            <LoadingOverlay active={isLoading()}>
                <div className="card-table shadow">
                    {coupons?.length > 0 ? (
                        <>
                            <CardTable
                                headers={headers}
                                keys={keys}
                                rows={coupons}
                                purpose={"coupons"}
                                sortBy={couponsSortBy}
                                setSortBy={setCouponsSortBy}
                                sortDirection={couponsSortDirection}
                                setSortDirection={setCouponsSortDirection}
                                actionColumn={true}
                                actions={renderActions}
                            />
                            <Pagination
                                currentPage={couponsCurrentPage}
                                setCurrentPage={handleCouponsPageClick}
                                totalCount={couponsTotalCount}
                                sizePerPage={itemsPerRequest}
                                handleItemsPerPage={handleItemsPerPage}
                            />
                        </>
                    ) : (
                        <MissingData
                            isFilterUsed={couponsFiltersUsed}
                            entity={t("coupons")}
                            includeText={!isLoading()}
                        />
                    )}
                </div>
            </LoadingOverlay>

            <AddPromoCodeModal
                isVisible={addPromoModal}
                setIsVisible={setAddPromoModal}
                addAction={increasePromos}
                couponId={selectedCoupon}
            />

            <CreateCouponOverlayModal
                isVisible={createCouponModal}
                setIsVisible={setCreateCouponModal}
                submitCallback={fetchData}
            />
        </>
    );
};

export default Coupons;
